import { axiosInstance } from "../plugins/axios";

const ACCESS_TOKEN_KEY = "alkokh_access_token";
const REFRESH_TOKEN_KEY = "alkokh_refresh_token";
const TOKEN_EXPIRY_KEY = "alkokh_token_expiry";
const USERNAME_KEY = "alkokh_username";
const USER_PROFILE_KEY = "alkokh_user_profile";
const SESSION_AUTH_KEY = "alkokh_session_authenticated";
const AUTH_PERSISTENCE_KEY = "alkokh_auth_persistence";

export type AuthStrategy = "session" | "token";
export type AuthPersistence = "local" | "session";

const AUTH_STORAGE_KEYS = [
  ACCESS_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  TOKEN_EXPIRY_KEY,
  USERNAME_KEY,
  USER_PROFILE_KEY,
  SESSION_AUTH_KEY,
];

function getStorage(persistence: AuthPersistence): Storage {
  return persistence === "local" ? localStorage : sessionStorage;
}

function hasAuthData(storage: Storage): boolean {
  return AUTH_STORAGE_KEYS.some(key => storage.getItem(key) !== null);
}

function getActivePersistence(): AuthPersistence {
  const sessionPersistence = sessionStorage.getItem(AUTH_PERSISTENCE_KEY);
  if (sessionPersistence === "session")
    return "session";

  const localPersistence = localStorage.getItem(AUTH_PERSISTENCE_KEY);
  if (localPersistence === "local")
    return "local";

  if (hasAuthData(sessionStorage))
    return "session";

  return "local";
}

function getActiveStorage(): Storage {
  return getStorage(getActivePersistence());
}

function readAuthItem(key: string): string | null {
  const persistence = getActivePersistence();
  const activeStorage = getStorage(persistence);
  const activeValue = activeStorage.getItem(key);

  if (activeValue !== null)
    return activeValue;

  if (persistence === "session")
    return null;

  return sessionStorage.getItem(key);
}

function writeAuthItem(key: string, value: string | null) {
  const storage = getActiveStorage();

  if (value !== null)
    storage.setItem(key, value);
  else
    storage.removeItem(key);
}

function clearAuthItems(storage: Storage) {
  AUTH_STORAGE_KEYS.forEach(key => storage.removeItem(key));
}

function safeParseJson<T>(value: string | null): T | null {
  if (!value)
    return null;

  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
}

export const authStorage = {
  getPersistence: getActivePersistence,
  setPersistence: (persistence: AuthPersistence) => {
    clearAuthItems(persistence === "local" ? sessionStorage : localStorage);
    localStorage.removeItem(AUTH_PERSISTENCE_KEY);
    sessionStorage.removeItem(AUTH_PERSISTENCE_KEY);
    getStorage(persistence).setItem(AUTH_PERSISTENCE_KEY, persistence);
  },
  getAccessToken: () => readAuthItem(ACCESS_TOKEN_KEY),
  setAccessToken: (t: string | null) => {
    writeAuthItem(ACCESS_TOKEN_KEY, t);
  },
  getRefreshToken: () => readAuthItem(REFRESH_TOKEN_KEY),
  setRefreshToken: (t: string | null) => {
    writeAuthItem(REFRESH_TOKEN_KEY, t);
  },
  getTokenExpiry: () => readAuthItem(TOKEN_EXPIRY_KEY),
  setTokenExpiry: (expiry: number | null) => {
    writeAuthItem(TOKEN_EXPIRY_KEY, expiry ? expiry.toString() : null);
  },
  getUsername: () => readAuthItem(USERNAME_KEY),
  setUsername: (username: string | null) => {
    writeAuthItem(USERNAME_KEY, username);
  },
  getUserProfile: <T = any>() => safeParseJson<T>(readAuthItem(USER_PROFILE_KEY)),
  setUserProfile: (user: any | null) => {
    writeAuthItem(USER_PROFILE_KEY, user ? JSON.stringify(user) : null);
  },
  getSessionAuthenticated: () => readAuthItem(SESSION_AUTH_KEY) === "1",
  setSessionAuthenticated: (value: boolean) => {
    writeAuthItem(SESSION_AUTH_KEY, value ? "1" : null);
  },
  clearTokens: () => {
    // Remove current token keys explicitly
    clearAuthItems(localStorage);
    clearAuthItems(sessionStorage);
    localStorage.removeItem(AUTH_PERSISTENCE_KEY);
    sessionStorage.removeItem(AUTH_PERSISTENCE_KEY);

    // Also remove any legacy token keys that might exist
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    sessionStorage.removeItem("access_token");
    sessionStorage.removeItem("refresh_token");

    // Clear any session data
    localStorage.removeItem("session");
    localStorage.removeItem("fullname");
    localStorage.removeItem("user_id");
    sessionStorage.removeItem("session");
    sessionStorage.removeItem("fullname");
    sessionStorage.removeItem("user_id");

    console.debug("Auth storage cleared");
  },
};

function normalizeAuthStrategy(value: unknown): AuthStrategy {
  const strategy = String(value ?? "").trim().toLowerCase();

  return strategy === "token" ? "token" : "session";
}

export function getAuthStrategy(): AuthStrategy {
  return normalizeAuthStrategy(import.meta.env.VITE_AUTH_STRATEGY);
}

function getTokenClientCredentials() {
  const clientId = String(import.meta.env.VITE_AUTH_TOKEN_CLIENT_ID ?? "").trim();
  const clientSecret = String(import.meta.env.VITE_AUTH_TOKEN_CLIENT_SECRET ?? "").trim();

  if (!clientId || !clientSecret) {
    throw new Error(
      "Missing OAuth client credentials. Set VITE_AUTH_TOKEN_CLIENT_ID and VITE_AUTH_TOKEN_CLIENT_SECRET.",
    );
  }

  return { clientId, clientSecret };
}

export const AUTH_ENDPOINTS = {
  SESSION_LOGIN: "/api/method/login",
  FRAPPE_OAUTH_TOKEN: "/api/method/frappe.integrations.oauth2.get_token",
  PET_APP_OAUTH_TOKEN: "/api/method/pet_app.api.auth_api.login_and_get_oauth_token",
  PET_APP_API_KEYS: "/api/method/pet_app.api.auth_api.login_and_get_api_keys",
} as const;

const TOKEN_LOGIN_ENDPOINT = AUTH_ENDPOINTS.FRAPPE_OAUTH_TOKEN;
const TOKEN_REFRESH_ENDPOINT = AUTH_ENDPOINTS.FRAPPE_OAUTH_TOKEN;
const SESSION_LOGIN_ENDPOINT = AUTH_ENDPOINTS.SESSION_LOGIN;

export interface AuthTokenResponse {
  access_token: string;
  expires_in: number;
  token_type: string;
  scope?: string;
  refresh_token?: string;
}

export interface PasswordLoginResponse {
  message?: string;
  home_page?: string;
  full_name?: string;
  user?: string;
  [key: string]: any;
}

export interface PasswordLoginCredentials {
  username: string;
  password: string;
}

export interface PasswordLoginOptions {
  remember?: boolean;
}

export type PasswordLoginResult =
  | { strategy: "session"; data: PasswordLoginResponse }
  | { strategy: "token"; data: AuthTokenResponse };

let isRefreshing = false;
let refreshQueue: Array<(token: string | null) => void> = [];

async function sessionPasswordLogin(
  credentials: PasswordLoginCredentials,
  options: PasswordLoginOptions = {},
): Promise<PasswordLoginResponse> {
  authStorage.setPersistence(options.remember ? "local" : "session");

  const response = await axiosInstance.post<PasswordLoginResponse>(
    SESSION_LOGIN_ENDPOINT,
    {
      usr: credentials.username,
      pwd: credentials.password,
    },
  );

  authStorage.setAccessToken(null);
  authStorage.setRefreshToken(null);
  authStorage.setTokenExpiry(null);
  authStorage.setUsername(credentials.username);
  authStorage.setSessionAuthenticated(true);

  return response.data;
}

async function tokenPasswordLogin(
  credentials: PasswordLoginCredentials,
  options: PasswordLoginOptions = {},
): Promise<AuthTokenResponse> {
  authStorage.setPersistence(options.remember ? "local" : "session");

  const { clientId, clientSecret } = getTokenClientCredentials();
  const formData = new URLSearchParams();
  formData.append("grant_type", "password");
  formData.append("client_id", clientId);
  formData.append("client_secret", clientSecret);
  formData.append("username", credentials.username);
  formData.append("password", credentials.password);

  const response = await axiosInstance.post<AuthTokenResponse>(
    TOKEN_LOGIN_ENDPOINT,
    formData.toString(),
    {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    },
  );

  authStorage.setSessionAuthenticated(false);
  authStorage.setAccessToken(response.data.access_token);
  authStorage.setRefreshToken(response.data.refresh_token ?? null);
  authStorage.setUsername(credentials.username);

  const expiryTime = Date.now() + Number(response.data.expires_in || 3600) * 1000;
  authStorage.setTokenExpiry(expiryTime);

  return response.data;
}

export async function passwordLogin(
  credentials: PasswordLoginCredentials,
  options: PasswordLoginOptions = {},
): Promise<PasswordLoginResult> {
  if (getAuthStrategy() === "token") {
    return {
      strategy: "token",
      data: await tokenPasswordLogin(credentials, options),
    };
  }

  return {
    strategy: "session",
    data: await sessionPasswordLogin(credentials, options),
  };
}

/**
 * Why the last refresh failed. `refreshAccessToken` resolves to `null` for two
 * completely different reasons, and the 401 handler used to treat them as one:
 *
 *   "rejected" — the server answered and refused the refresh token. The session
 *                really is over, so logging out is correct.
 *   "network"  — no answer arrived at all. The session is very probably still
 *                valid; we simply could not ask. Logging out here is the single
 *                most destructive thing the app can do offline, because the
 *                moment the connection returns is exactly when it would fire,
 *                wiping tokens and any unsent work with them.
 *
 * Kept as a module-level value rather than a changed return type so no existing
 * caller of `refreshAccessToken` has to change.
 */
export type RefreshFailureKind = "network" | "rejected";

/**
 * Move a session-only login onto durable storage, carrying its values across.
 *
 * WHY. Without "remember me" the tokens live in `sessionStorage`, which a browser
 * crash or a closed tab erases. That is a reasonable default on a shared desk,
 * and unusable on a device expected to work through a 24-hour outage: the user
 * would come back to a login screen with no network to log in against, while the
 * device still holds unsent sales.
 *
 * ⚠️ `authStorage.setPersistence` CLEARS the store it is switching away from, so
 * calling it alone would delete the very tokens we are trying to preserve. This
 * reads first, switches, then writes back.
 *
 * Returns true when a promotion actually happened.
 */
export function promoteAuthPersistenceToDurable(): boolean {
  if (getActivePersistence() === "local")
    return false;

  const carried = AUTH_STORAGE_KEYS.map(key => [key, readAuthItem(key)] as const);

  authStorage.setPersistence("local");

  for (const [key, value] of carried) {
    if (value !== null)
      writeAuthItem(key, value);
  }

  console.info("Login moved to durable storage so it survives a crash while offline.");

  return true;
}

let lastRefreshFailureKind: RefreshFailureKind | null = null;

export function getLastRefreshFailureKind(): RefreshFailureKind | null {
  return lastRefreshFailureKind;
}

export async function refreshAccessToken(): Promise<string | null> {
  if (isRefreshing) {
    return new Promise((resolve) => {
      refreshQueue.push((token) => resolve(token));
    });
  }

  isRefreshing = true;
  try {
    const refreshToken = authStorage.getRefreshToken();
    if (!refreshToken) throw new Error("No refresh token");

    const { clientId, clientSecret } = getTokenClientCredentials();
    const formData = new URLSearchParams();
    formData.append("grant_type", "refresh_token");
    formData.append("client_id", clientId);
    formData.append("client_secret", clientSecret);
    formData.append("refresh_token", refreshToken);

    const response = await axiosInstance.post<AuthTokenResponse>(
      TOKEN_REFRESH_ENDPOINT,
      formData.toString(),
      {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      },
    );

    authStorage.setAccessToken(response.data.access_token);
    authStorage.setRefreshToken(response.data.refresh_token || refreshToken);
    /**
     * WARNING: FALLBACK ON PURPOSE. The login path has always had one; this one did not,
     * and a missing expires_in stored the string "NaN" - after which Date.now() >= NaN is
     * false forever, so the token is treated as eternally valid, never refreshed, and the
     * first sign of trouble is a 401 mid-shift. The backend does always send it (at the
     * TOP LEVEL, not under `message`), so this is belt and braces, not a workaround.
     */
    const expiresIn = Number(response.data.expires_in);
    const expiryTime = Date.now() + (Number.isFinite(expiresIn) && expiresIn > 0 ? expiresIn : 3600) * 1000;
    authStorage.setTokenExpiry(expiryTime);

    lastRefreshFailureKind = null;

    // notify queued requests
    refreshQueue.forEach((cb) => cb(response.data.access_token));
    refreshQueue = [];
    return response.data.access_token;
  } catch (err: any) {
    // A refusal carries a response; a dead network does not. This one line is the
    // whole basis on which the 401 handler decides whether to end the session.
    lastRefreshFailureKind = err?.response ? "rejected" : "network";

    // Don't clear tokens here - let the 401 handler decide when to logout
    // This preserves the refresh token for retry attempts
    console.error("Token refresh request failed:", err);
    refreshQueue.forEach((cb) => cb(null));
    refreshQueue = [];
    return null;
  } finally {
    isRefreshing = false;
  }
}

export function isTokenExpired(): boolean {
  const expiry = authStorage.getTokenExpiry();
  if (!expiry) return true;
  return Date.now() >= parseInt(expiry, 10);
}

export function getAccessToken(): string | null {
  return authStorage.getAccessToken();
}

/**
 * Session-expiry signal between the axios response interceptor and the router
 * guard. It lives here because `authService` imports only axios, so both
 * `services/httpClient` and `plugins/1.router` can depend on it without a cycle
 * (both already import `authStorage` from this file).
 *
 * Why a signal instead of `router.push` from the interceptor: the 403 that
 * reveals a dead session is usually raised by `get_current_access`, which the
 * guard itself awaits. Pushing a navigation from inside an in-flight navigation
 * makes the outer one abort, and because the default layout wraps `<RouterView>`
 * in `<Transition mode="out-in">` the outgoing component is already unmounted —
 * leaving a blank content area with the sidebar intact. So the interceptor only
 * records the fact; whoever is in a position to navigate safely acts on it.
 */
let sessionExpired = false;
let navigationInFlight = false;

export function markSessionExpired() {
  sessionExpired = true;
}

/** Reads AND clears the flag, so one expiry produces exactly one redirect. */
export function consumeSessionExpired() {
  const wasExpired = sessionExpired;

  sessionExpired = false;

  return wasExpired;
}

export function setNavigationInFlight(value: boolean) {
  navigationInFlight = value;
}

/**
 * True when a router navigation is currently resolving. The interceptor uses
 * this to decide between "let the guard redirect" and "no guard is running, so I
 * must navigate myself" (e.g. a 403 from the 15s notification poll).
 */
export function isNavigationInFlight() {
  return navigationInFlight;
}
